import { useCallback, useEffect, useState } from 'react';
import defaultApiFetch from '../../../utils/apiFetch';
import { toPublicUploadIconUrl } from '../core';

/**
 * 管理后台上传图标库（member_icons）
 * @param {{ enabled?: boolean, apiFetch?: Function }} opts
 */
export function useUploadedIcons({ enabled = true, apiFetch = defaultApiFetch } = {}) {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!enabled) {
      setIcons([]);
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/icons');
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || '加载上传图标失败');
      }
      const list = (data.icons || []).map((ic) => ({
        ...ic,
        url: toPublicUploadIconUrl(ic.file_path),
        value: ic.id,
        label: ic.name
      }));
      setIcons(list);
      return list;
    } catch (e) {
      setError(e.message || String(e));
      setIcons([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled, apiFetch]);

  useEffect(() => {
    reload();
  }, [reload]);

  const uploadIcon = useCallback(async (file, { name, description } = {}) => {
    const form = new FormData();
    form.append('icon', file);
    form.append('name', name || String(file.name || 'icon').replace(/\.[^.]+$/, ''));
    form.append('description', description || '');
    const res = await apiFetch('/api/admin/icons/upload', { method: 'POST', body: form });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      throw new Error(data.error || '上传失败');
    }
    await reload();
    return data.icon || null;
  }, [apiFetch, reload]);

  const updateIcon = useCallback(async (id, { name, description } = {}) => {
    const res = await apiFetch(`/api/admin/icons/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      throw new Error(data.error || '更新失败');
    }
    await reload();
    return data.icon || null;
  }, [apiFetch, reload]);

  const deleteIcon = useCallback(async (id) => {
    const res = await apiFetch(`/api/admin/icons/${id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      throw new Error(data.error || '删除失败');
    }
    await reload();
    return true;
  }, [apiFetch, reload]);

  return { icons, loading, error, reload, uploadIcon, updateIcon, deleteIcon };
}
